<script lang="ts">
  import { createEventDispatcher, onDestroy, onMount } from 'svelte';

  import type { DataTableHeader, DataTableRow } from 'carbon-components-svelte/src/DataTable/DataTable.svelte';
  import debounce from 'lodash/debounce';

  import { DataTable, DataTableSkeleton, Pagination } from 'carbon-components-svelte';

  import ExportCsvModal from './ExportCsvModal.svelte';
  import TableCell from './TableCell.svelte';
  import TableToolbar from './TableToolbar.svelte';
  import LoadingSpinner from '../LoadingSpinner.svelte';

  import { noopSort, parseSortEvent } from '../../utils';

  import { mergeCellRenderers, type CellRenderer } from './cell-renderers';
  import { createRouteSync, type RouteSync } from './route-sync';
  import { createClientStrategy, createServerStrategy, type ExportRequest, type TableQuery, type TableStrategy } from './table-controller';
  import { getExportableColumns, type CsvCellFormatter } from './table-csv';
  import type {
    CellType,
    ExportColumnOption,
    OrderDirection,
    TableChangePayload,
    TableColumn,
    TableExportPayload,
    TableFilter,
  } from './table-types';

  type HeaderClickEvent = CustomEvent<{ header: DataTableHeader; sortDirection?: 'none' | 'ascending' | 'descending' }>;

  export let headers: TableColumn[] = [],
    rows: DataTableRow[] = [],
    size: 'compact' | 'short' | 'medium' | 'tall' = 'medium',
    sortable = true,
    searchable = true,
    searchPlaceholder = 'Search',
    emptyText = 'No items to display.',
    loading = false,
    paginated = false,
    page = 1,
    pageSize = 25,
    pageSizes: number[] = [10, 25, 50],
    /** Provide (even `0`) to opt into server-side mode: the parent owns data/search/sort via `change`/`export` events. */
    totalItems: number | null = null,
    searchDebounceMs = 400,
    exportable = false,
    csvFilename = 'export',
    /** Overrides the export column list (defaults to the labeled headers); needed when the CSV schema differs from the display columns. */
    exportColumns: ExportColumnOption[] | null = null,
    /** Live filters shown in the toolbar; they also seed the export modal. */
    filters: TableFilter[] = [],
    /** Consumer cell renderers keyed by column `type`; merged over the built-ins, so these win and unspecified types fall back to ours. */
    cellRenderers: Record<string, CellRenderer> = {},
    /** Opt into URL sync (empty = off): search/paging/filters are read from and written to query params, namespaced by this prefix (e.g. `history.page`). */
    routePrefix = '',
    formatCsvCell: CsvCellFormatter = (row, header) => (row as Record<string, unknown>)[header.key],
    expandable = false,
    expandAllToggle = false,
    nonExpandableRowIds: (string | number)[] = [],
    expandedRowIds: (string | number)[] = [];

  const dispatch = createEventDispatcher<{ change: TableChangePayload; export: TableExportPayload }>();

  const serverSide = totalItems !== null;
  const strategy: TableStrategy = serverSide
    ? createServerStrategy({
        emitChange: (query) => dispatch('change', query),
        emitExport: (payload) => dispatch('export', payload),
      })
    : createClientStrategy({
        getExportConfig: () => ({ headers, exportColumns, csvFilename, formatCsvCell }),
      });

  let searchText = '',
    orderBy: string | null = null,
    orderDirection: OrderDirection = null,
    filterValues: Record<string, string[]> = {},
    routeSync: RouteSync | null = null,
    ready = false,
    exportModalOpen = false,
    expandAll = false;

  const currentQuery = (): TableQuery => ({ page, pageSize, orderBy, orderDirection, searchText, filters: filterValues });
  const routeState = () => ({ searchText, page, pageSize, filters: filterValues });

  const commitQuery = () => {
    if (!ready) {
      return;
    }

    strategy.onQueryChange(currentQuery());
  };

  const commitSearchQuery = debounce(commitQuery, searchDebounceMs);

  const hydrateFromUrl = () => {
    const state = routeSync?.read({ page, pageSize });
    if (!state) {
      return;
    }

    searchText = state.searchText;
    page = state.page;
    pageSize = state.pageSize;
    filterValues = state.filters;
  };

  const handlePopstate = () => {
    if (!routeSync) {
      return;
    }

    routeSync.writeDebounced.cancel();
    hydrateFromUrl();
    commitQuery();
  };

  const sameSelection = (a: string[] = [], b: string[] = []): boolean =>
    a.length === b.length && [...a].sort().join('\u0000') === [...b].sort().join('\u0000');

  const handleSearch = (event: Event) => {
    searchText = ((event.target as HTMLInputElement | null)?.value ?? '').trim();
    page = 1;
    routeSync?.writeDebounced(routeState());
    commitSearchQuery();
  };

  const handleFilterSelect = (key: string, values: string[]) => {
    // MultiSelect fires a `select` event on init; ignore no-op changes so server mode doesn't refetch needlessly.
    if (sameSelection(filterValues[key], values)) {
      return;
    }

    filterValues = { ...filterValues, [key]: values };
    page = 1;
    routeSync?.write(routeState());
    commitQuery();
  };

  const handlePaginationUpdate = (event: CustomEvent<{ page: number; pageSize: number }>) => {
    const changed = event.detail.page !== page || event.detail.pageSize !== pageSize;
    page = event.detail.page;
    pageSize = event.detail.pageSize;

    if (!changed) {
      return;
    }

    routeSync?.write(routeState());
    commitQuery();
  };

  const handleHeaderClick = (event: HeaderClickEvent) => {
    if (!strategy.remoteSort) {
      return;
    }

    parseSortEvent(event, (sort) => {
      orderBy = sort.orderBy;
      orderDirection = sort.orderDirection;
      page = 1;
      commitQuery();
    });
  };

  const runExport = (event: CustomEvent<ExportRequest>) => strategy.runExport(event.detail, { rows, query: currentQuery() });

  const toggleExpandAll = () => {
    if (allExpanded) {
      expandAll = false;
      expandedRowIds = [];
    } else {
      expandAll = true;
      expandedRowIds = [...expandableRowIds];
    }
  };

  const onRowExpandToggle = (event: CustomEvent<{ expanded: boolean }>) => {
    if (!event.detail.expanded) {
      expandAll = false;
    } else if (expandableRowIds.length > 0 && expandableRowIds.every((id) => expandedRowIds.includes(id))) {
      expandAll = true;
    }
  };

  onMount(() => {
    if (routePrefix) {
      routeSync = createRouteSync(
        routePrefix,
        filters.map((filter) => filter.key)
      );
      hydrateFromUrl();
    }

    ready = true;

    if (routePrefix && serverSide) {
      commitQuery();
    }
  });

  onDestroy(() => {
    commitSearchQuery.cancel();
    routeSync?.writeDebounced.cancel();
  });

  $: displayRows = strategy.displayRows(rows, filterValues, searchText);
  $: paginationTotal = serverSide ? totalItems ?? 0 : displayRows.length;
  $: datasetEmpty = serverSide ? (totalItems ?? 0) === 0 : rows.length === 0;

  $: tableHeaders = (
    strategy.remoteSort && sortable
      ? headers.map((header) => ('key' in header && header.sort !== false && !header.empty ? { ...header, sort: noopSort } : header))
      : headers
  ) as DataTableHeader[];
  $: usesClientPaging = paginated && strategy.clientPaging;
  $: pagingProps = usesClientPaging ? { page, pageSize } : {};
  $: renderers = mergeCellRenderers(cellRenderers);
  $: availableExportColumns = exportColumns ?? getExportableColumns(headers);

  $: cellRendererByKey = Object.fromEntries(
    headers
      .filter((header): header is TableColumn & { key: string; type: CellType } => 'key' in header && Boolean(header.type))
      .map((header) => [header.key, renderers[header.type]])
      .filter(([, renderer]) => Boolean(renderer))
  ) as Record<string, CellRenderer>;
  $: columnByKey = Object.fromEntries(
    headers.filter((header): header is TableColumn & { key: string } => 'key' in header).map((header) => [header.key, header])
  ) as Record<string, TableColumn>;

  $: showSkeleton = loading && displayRows.length === 0;
  $: skeletonSizeProps = size === 'medium' ? {} : { size };

  $: expandableRowIds = displayRows.map((row) => row.id).filter((id) => !nonExpandableRowIds.includes(id));
  $: allExpanded = expandableRowIds.length > 0 && expandableRowIds.every((id) => expandedRowIds.includes(id));
  $: if (expandAll) expandedRowIds = [...expandableRowIds];

  $: hasActiveQuery = Boolean(searchText) || Object.values(filterValues).some((values) => values.length > 0);
  $: controlsDisabled = !loading && !hasActiveQuery && datasetEmpty;
  $: showToolbar = searchable || exportable || (expandable && expandAllToggle) || filters.length > 0;
</script>

<svelte:window on:popstate={handlePopstate} />

{#if showSkeleton}
  <div class="skeleton-wrapper{size === 'medium' ? '' : ` ${size}`}">
    <DataTableSkeleton showHeader={false} headers={tableHeaders} rows={pageSize} {...skeletonSizeProps} />
  </div>
{:else}
  <LoadingSpinner {loading} small>
    <DataTable
      headers={tableHeaders}
      rows={displayRows}
      {sortable}
      {size}
      {expandable}
      {nonExpandableRowIds}
      {...pagingProps}
      bind:expandedRowIds
      on:click:header={handleHeaderClick}
      on:click:row--expand={onRowExpandToggle}
    >
      <svelte:fragment slot="cell" let:cell let:row>
        <slot name="cell" {cell} {row}>
          <TableCell {cell} {row} column={columnByKey[cell.key]} renderer={cellRendererByKey[cell.key]} />
        </slot>
      </svelte:fragment>
      <svelte:fragment slot="expandedRow" let:row>
        <slot name="expandedRow" {row} />
      </svelte:fragment>
      {#if showToolbar}
        <TableToolbar
          {filters}
          {filterValues}
          {size}
          {searchable}
          {searchPlaceholder}
          {searchText}
          {exportable}
          canExport={paginationTotal > 0}
          {expandable}
          {expandAllToggle}
          {allExpanded}
          {controlsDisabled}
          expandDisabled={!expandableRowIds.length}
          onFilterSelect={handleFilterSelect}
          onSearch={handleSearch}
          onToggleExpandAll={toggleExpandAll}
          onOpenExport={() => (exportModalOpen = true)}
        />
      {/if}
    </DataTable>
  </LoadingSpinner>

  {#if paginated && paginationTotal > 0}
    <Pagination {page} {pageSize} {pageSizes} totalItems={paginationTotal} on:update={handlePaginationUpdate} />
  {/if}

  {#if !displayRows.length && emptyText}
    <div class="empty-state">{emptyText}</div>
  {/if}
{/if}

{#if exportable && exportModalOpen}
  <ExportCsvModal
    bind:open={exportModalOpen}
    columns={availableExportColumns}
    {filters}
    initialSelections={filterValues}
    searchActive={Boolean(searchText)}
    on:confirm={runExport}
  />
{/if}

<style>
  .skeleton-wrapper.short :global(.bx--table-toolbar),
  .skeleton-wrapper.short :global(.bx--btn.bx--skeleton) {
    height: 2rem !important;
    min-height: 2rem !important;
  }

  .empty-state {
    align-content: center;
    background-color: var(--cds-layer);
    color: var(--cds-text-secondary);
    min-height: var(--cds-spacing-09);
    text-align: center;
    padding: 1rem;
  }
</style>
